'use client';

import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import styles from './HalfCarouselHero.module.scss';

export type CarouselHeroImage = {
    url: string;
    alt: string;
};

export type HalfCarouselHeroSlice = {
    type?: 'half-carousel-hero';
    imageUrl?: string;
    imageAlt?: string;
    images?: CarouselHeroImage[];
    /** Example: "66dvh" or "100dvh" */
    height?: string;
    /** Auto-advance interval in ms. Recommended 4500–7000. Default 5000 */
    intervalMs?: number;
    /** Fade duration in ms. Default 1100 */
    fadeMs?: number;
};

type Props = {
    data: HalfCarouselHeroSlice;
    className?: string;
};

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setReduced(mq.matches);
        onChange();
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, []);

    return reduced;
}

function HalfCarouselHeroComponent({ data, className }: Props) {
    const prefersReducedMotion = usePrefersReducedMotion();

    const images = useMemo<CarouselHeroImage[]>(() => {
        if (Array.isArray(data.images) && data.images.length > 0) return data.images;
        if (data.imageUrl) {
            return [{ url: data.imageUrl, alt: data.imageAlt ?? 'Carousel image' }];
        }
        return [];
    }, [data.images, data.imageUrl, data.imageAlt]);

    const intervalMs = typeof data.intervalMs === 'number' ? data.intervalMs : 5000;
    const fadeMs = typeof data.fadeMs === 'number' ? data.fadeMs : 1100;

    const [activeIndex, setActiveIndex] = useState(0);
    const [prevIndex, setPrevIndex] = useState<number | null>(null);
    const [isFading, setIsFading] = useState(false);

    const rootRef = useRef<HTMLElement | null>(null);
    const isVisibleRef = useRef(true);
    const isTabVisibleRef = useRef(true);

    // Reset safely if images change
    useEffect(() => {
        if (images.length === 0) return;
        setActiveIndex(0);
        setPrevIndex(null);
        setIsFading(false);
    }, [images.length]);

    // Pause when off-screen
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;

        const io = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                isVisibleRef.current = !!entry?.isIntersecting;
            },
            { threshold: 0.15 }
        );

        io.observe(el);
        return () => io.disconnect();
    }, []);

    // Pause when tab hidden
    useEffect(() => {
        const onVis = () => {
            isTabVisibleRef.current = document.visibilityState === 'visible';
        };
        onVis();
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);

    // Preload helper (preload next image in the background)
    const preload = (url: string) => {
        if (typeof window === 'undefined') return;
        const img = new window.Image();
        img.decoding = 'async';
        img.loading = 'eager';
        img.src = url;
    };

    // Auto advance with cross-fade
    useEffect(() => {
        if (prefersReducedMotion) return;
        if (images.length <= 1) return;

        const id = window.setInterval(() => {
            if (!isVisibleRef.current) return;
            if (!isTabVisibleRef.current) return;
            if (isFading) return; // avoid overlapping fades

            const next = (activeIndex + 1) % images.length;

            // Preload next right before transition
            preload(images[next].url);

            // Start fade: show previous under the active image
            setPrevIndex(activeIndex);
            setActiveIndex(next);
            setIsFading(true);

            // End fade after fadeMs (cleanup prev layer)
            window.setTimeout(() => {
                setPrevIndex(null);
                setIsFading(false);
            }, fadeMs + 40);
        }, intervalMs);

        return () => window.clearInterval(id);
    }, [activeIndex, fadeMs, images, intervalMs, isFading, prefersReducedMotion]);

    if (images.length === 0) return null;

    const computedHeight = data.height?.trim() || '66dvh';
    const nextIndex = images.length > 1 ? (activeIndex + 1) % images.length : 0;

    return (
        <section
            ref={(node) => {
                rootRef.current = node;
            }}
            className={[styles.root, className].filter(Boolean).join(' ')}
            style={
                {
                    height: computedHeight,
                    // pass fade duration to CSS (no reflow heavy work)
                    ['--fade-ms' as any]: `${fadeMs}ms`,
                } as React.CSSProperties
            }
            aria-label="Image carousel hero"
        >
            <div className={styles.viewport}>
                {/* Previous layer (only rendered during fade) */}
                {prevIndex !== null && images[prevIndex] && (
                    <div className={[styles.layer, styles.prevLayer].join(' ')}>
                        <Image
                            src={images[prevIndex].url}
                            alt={images[prevIndex].alt}
                            fill
                            sizes="100vw"
                            className={styles.image}
                            // no need for priority on previous
                            loading="eager"
                        />
                    </div>
                )}

                {/* Active layer (fades in) */}
                <div
                    className={[
                        styles.layer,
                        styles.activeLayer,
                        isFading ? styles.fadingIn : styles.stable,
                    ].join(' ')}
                >
                    <Image
                        src={images[activeIndex].url}
                        alt={images[activeIndex].alt}
                        fill
                        sizes="100vw"
                        className={styles.image}
                        // LCP friendly: only first slide priority
                        priority={activeIndex === 0}
                        loading={activeIndex === 0 ? 'eager' : 'lazy'}
                    />
                </div>

                {/* Optional: hidden prefetch hint (very cheap) */}
                {images.length > 1 && (
                    <link rel="preload" as="image" href={images[nextIndex].url} />
                )}
            </div>

            {images.length > 1 && (
                <div className={styles.dots} aria-hidden="true">
                    {images.map((_, i) => (
                        <span
                            key={i}
                            className={[styles.dot, i === activeIndex ? styles.dotActive : ''].join(' ')}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

export default memo(HalfCarouselHeroComponent);
